import { useEffect } from "react";

import { useGetUserAssignmentQuery } from "api/generated";

import { getAdmissionTestLabel, parseAssignmentDetailTitle } from "utils/common/assignmentDetail";
import { processApiError } from "utils/processApiError";

// AIDEV-NOTE: Detail úlohy čte `currentData` (ne `data`): při načítání nových argumentů `data` drží úlohu z předchozího
// ID, takže by se po Zpět/Vpřed zobrazila stará úloha pod novým ID. `lastAssignmentData` (z `data`) je jen pro to, co
// se při přechodu mezi úlohami nesmí zahodit, např. předmět pro šipky Uložených a Vyřazených. [TD-487]
// AIDEV-NOTE: `isLoading` znamená "dotaz pro toto ID ještě nic nevrátil". RTK `isLoading` je při přechodu na nové ID
// false (drží `data` staré úlohy) a `assignmentData === null` neodliší načítání od prázdného výsledku či chyby, stránka
// by pak ukazovala spinner navždy. Prázdný výsledek a chybu stránky předají jako `null` do `AssignmentDetailPractice`,
// který ukáže hlášku. Bez ID (`skip`) dotaz neběží a nic nenačítá, takže `isLoading` je false. Známé a přijaté:
// u ID, jehož položka v cache dřív selhala, se hláška krátce probliskne. [TD-487]
// AIDEV-NOTE: Prázdný výsledek bez chyby je úloha, kterou backend skryl (`deleted`), protože Zóna její test studentovi
// nevrací, typicky dočasně vypnuté vzorové testy. Po zapnutí testu se úloha vrátí, proto `isUnavailable` a vlastní
// hláška místo chyby načtení. Cizí nebo neexistující ID vypadá stejně, to je přijaté. [TD-494]
// Titulek (předmět, test, pořadí úlohy) je stejný na všech šesti stránkách detailu, proto se počítá tady.
export const useUserAssignmentDetail = (userAssignmentId: string | undefined) => {
  const skip = !userAssignmentId;
  const { currentData, data, isError, error } = useGetUserAssignmentQuery(
    { assignmentId: userAssignmentId ?? "" },
    { skip },
  );

  useEffect(() => {
    if (error === undefined) return;

    processApiError(error);
  }, [error]);

  const assignmentData = currentData?.userAssignments.items.at(0) ?? null;
  const title = parseAssignmentDetailTitle(
    assignmentData?.subject?.appAbbr ?? undefined,
    getAdmissionTestLabel(assignmentData?.assignment?.general?.admissionTest) ?? undefined,
    assignmentData?.assignment?.general?.testOrder ?? undefined,
  );

  return {
    assignmentData,
    lastAssignmentData: data?.userAssignments.items.at(0) ?? null,
    isLoading: currentData === undefined && !isError && !skip,
    isUnavailable: currentData !== undefined && assignmentData === null,
    title,
  };
};
